import { useState, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import axios from "axios";
import LandingPage from "@/pages/LandingPage";
import Dashboard from "@/pages/Dashboard";
import AdminCosts from "@/pages/AdminCosts";
import AdminUsers from "@/pages/AdminUsers";
import ProducerSpotlight from "@/pages/ProducerSpotlight";
import YouTubeCallback from "@/pages/YouTubeCallback";
import PrivacyPolicy from "@/pages/PrivacyPolicy";
import TermsAndConditions from "@/pages/TermsAndConditions";
import About from "@/pages/About";
import Footer from "@/components/Footer";
import { Toaster } from "@/components/ui/sonner";
import { ThemeProvider } from "@/lib/ThemeContext";
import { clearAuthToken, getAuthToken } from "@/lib/auth";

const BACKEND_URL =
  process.env.REACT_APP_BACKEND_URL || process.env.REACT_APP_API_BASE_URL;
const API_BASE_URL = (BACKEND_URL || window.location.origin).replace(/\/$/, "");
export const API = `${API_BASE_URL}/api`;

if (!BACKEND_URL) {
  console.warn("Missing REACT_APP_BACKEND_URL / REACT_APP_API_BASE_URL. Falling back to current origin for API requests.");
}

// Axios interceptor for auth token
axios.interceptors.request.use(
  (config) => {
    const token = getAuthToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (timezone) {
      config.headers["X-Timezone"] = timezone;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

axios.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error?.response?.status;
    const token = getAuthToken();
    if (status === 401 && token) {
      clearAuthToken();
      if (window.location.pathname !== "/") {
        window.location.assign("/");
      }
    }
    return Promise.reject(error);
  }
);

const DEV_BYPASS =
  process.env.REACT_APP_DEV_BYPASS === "true" &&
  (window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1");

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(DEV_BYPASS);
  const [isAdmin, setIsAdmin] = useState(DEV_BYPASS);
  const [loading, setLoading] = useState(!DEV_BYPASS);

  useEffect(() => {
    if (DEV_BYPASS) return;
    const checkAuth = async () => {
      const token = getAuthToken();
      if (token) {
        try {
          const me = await axios.get(`${API}/auth/me`);
          setIsAdmin(Boolean(me?.data?.is_admin));
          setIsAuthenticated(true);
        } catch (error) {
          clearAuthToken();
          setIsAuthenticated(false);
          setIsAdmin(false);
        }
      } else {
        setIsAdmin(false);
      }
      setLoading(false);
    };
    checkAuth();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <ThemeProvider>
      <div className="App flex flex-col min-h-screen transition-colors duration-500">
        <Toaster position="top-right" richColors />
        <BrowserRouter>
        <div className="flex-grow">
          <Routes>
            <Route
              path="/"
              element={
                isAuthenticated ? (
                  <Navigate to="/dashboard" replace />
                ) : (
                  <LandingPage setIsAuthenticated={setIsAuthenticated} />
                )
              }
            />
            <Route
              path="/spotlight"
              element={<ProducerSpotlight />}
            />
            <Route
              path="/admin/costs"
              element={
                isAuthenticated && isAdmin ? (
                  <AdminCosts />
                ) : (
                  <Navigate to={isAuthenticated ? "/dashboard" : "/"} replace />
                )
              }
            />
            <Route
              path="/admin/users"
              element={
                isAuthenticated && isAdmin ? (
                  <AdminUsers />
                ) : (
                  <Navigate to={isAuthenticated ? "/dashboard" : "/"} replace />
                )
              }
            />
            <Route
              path="/dashboard"
              element={
                isAuthenticated ? (
                  <Dashboard setIsAuthenticated={setIsAuthenticated} />
                ) : (
                  <Navigate to="/" replace />
                )
              }
            />
            <Route
              path="/youtube-callback"
              element={
                isAuthenticated ? (
                  <YouTubeCallback />
                ) : (
                  <Navigate to="/" replace />
                )
              }
            />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/terms" element={<TermsAndConditions />} />
            <Route path="/about" element={<About />} />
          </Routes>
        </div>
          <Footer />
        </BrowserRouter>
      </div>
    </ThemeProvider>
  );
}

export default App;
